import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount } from '@azure/msal-react';
import { getGroupsFromStorage } from '../utilities/StorageUtils';
import { groupAccess } from '../utilities/authConfig';
import { Utils } from '../utilities/Utils';
import type { WorkPreparationState } from '../api/WorkPreparationState';
import { Department } from '../api/generated/model/department';
import type { KontrollCreateDto } from '../api/generated/model/kontrollCreateDto';
import type { ProjectDto } from '../api/generated/model/projectDto';
import { ProjectStatus } from '../api/generated/model/projectStatus';
import type { WorkPreparationCompletionContract } from '../api/generated/model/workPreparationCompletionContract';
import type { TableOfContentsDto as TableOfContents } from '../api/generated/model/tableOfContentsDto';
import type { RescuePlanDto as RescuePlanModel } from '../api/generated/model/rescuePlanDto';
import type { RiskAssesmentContainerDto as RiskAssesmentContainer } from '../api/generated/model/riskAssesmentContainerDto';
import type { FactsAboutScaffoldingDto as FactsAboutScaffolding } from '../api/generated/model/factsAboutScaffoldingDto';
import type { SelfControlContainerDto as SelfControlContainerModel } from '../api/generated/model/selfControlContainerDto';
import type { PlanForConductDto as PlanForConduct } from '../api/generated/model/planForConductDto';
import type { FactsAboutWeatherProtectionDto as FactsAboutWeatherProtection } from '../api/generated/model/factsAboutWeatherProtectionDto';
import type { SelfControlWeatherProtectionContainerDto as SelfControlWeatherProtectionContainerModel } from '../api/generated/model/selfControlWeatherProtectionContainerDto';
import type { Monteringsanalys as MonteringsanalysModel } from '../api/generated/model/monteringsanalys';
import {
  useGetApiProjectsIdId,
  useGetApiProjectsGetProjectStatusProjectId,
} from '../api/generated/endpoints/project/project';
import {
  getApiTableOfContentsProjectId,
  postApiTableOfContents,
} from '../api/generated/endpoints/table-of-contents/table-of-contents';
import { getApiRescueplanProjectId } from '../api/generated/endpoints/rescue-plan/rescue-plan';
import { getApiRiskAssesmentProjectId } from '../api/generated/endpoints/risk-assesment/risk-assesment';
import { getApiFactsAboutScaffoldingProjectId } from '../api/generated/endpoints/facts-about-scaffolding/facts-about-scaffolding';
import { getApiSelfControlContainerProjectId } from '../api/generated/endpoints/self-control-container/self-control-container';
import { getApiPlanForConductProjectId } from '../api/generated/endpoints/plan-for-conduct/plan-for-conduct';
import { getApiFactsAboutWeatherProtectionProjectId } from '../api/generated/endpoints/facts-about-weather-protection/facts-about-weather-protection';
import { getApiSelfControlWeatherProtectionContainerProjectId } from '../api/generated/endpoints/self-control-weather-protection/self-control-weather-protection';
import {
  getApiMonteringsanalysProjectId,
  postApiMonteringsanalysSignProjectId,
  putApiMonteringsanalys,
} from '../api/generated/endpoints/monteringsanalys/monteringsanalys';
import { postApiSharepointPostProjectId } from '../api/generated/endpoints/share-point/share-point';
import {
  postApiEmailNotifyProjectContact,
  postApiEmailNotifyTeamLeader,
} from '../api/generated/endpoints/email/email';
import {
  changeProjectStatus,
  getPreWorkControl,
  getWorkPreparationCompletionDefinitions,
  getWorkPreparationReadiness,
  signScaffoldingSelfControl as postScaffoldingSelfControlSignature,
  signWeatherProtectionSelfControl as postWeatherProtectionSelfControlSignature,
} from '../api/workPreparationClient';
import { generateWorkPreparationDocuments, printWorkPreparationDocuments } from '../api/documents';
import { useQueryWithState } from '../hooks/useQueryWithState';
import { toastError, toastInfo, toastSuccess } from '../utilities/messageUtils';
import {
  handleSelfControlWeatherProtectionDataUpdate,
  handleFactAboutScaffoldingDataUpdate,
  handleSelfControlDataUpdate,
  handleRescuePlanDataUpdate,
  handlePlanForConductDataUpdate,
  handleRiskAssesmentDataUpdate,
  handleFactsWeatherProtectionDataUpdate,
} from '../components/pages/projects/projectlist/projectitempage/ProjectItemPageUpdateFunctions';
import { applyWorkPrepDateFallbacks, buildWorkPreparationSnapshot } from './snapshot';
import {
  isArchivedProject,
  isClosedOrHigher,
  isReadyToSignSelfControls,
  resolveHasMonteringsanalys,
} from './readiness';
import type { UseWorkPreparationResult } from './types';

export const useWorkPreparation = (projectId: number): UseWorkPreparationResult => {
  const [factAboutScaffoldingCompletionStatus, setFactAboutScaffoldingCompletionStatus] = useState(false);
  const [selfControlCompletionStatus, setSelfControlCompletionStatus] = useState(false);
  const [rescuePlanCompletionStatus, setRescuePlanCompletionStatus] = useState(false);
  const [planForConductCompletionStatus, setPlanForConductCompletionStatus] = useState(false);
  const [riskAssesmentCompletionStatus, setRiskAssesmentCompletionStatus] = useState(false);
  const [riskAssesmentWorkerCompletionStatus, setRiskAssesmentWorkerCompletionStatus] = useState(false);
  const [factsAboutWeatherProtectionCompletionStatus, setFactsWeatherProtectionCompletionStatus] = useState(false);
  const [selfControlWeatherProtectionCompletionStatus, setselfControlWeatherProtectionCompletionStatus] = useState(false);
  const [dimensionDocUpdated, setDimensionDocUpdated] = useState(false);
  const [userRole, setUserRole] = useState<'worker' | 'management'>('management');
  const [projectNotFoundError, setProjectNotFoundError] = useState<Error | null>(null);

  const activeAccount = useAccount();
  const myGroups = useMemo(
    () => activeAccount ? getGroupsFromStorage(activeAccount) ?? [] : [],
    [activeAccount],
  );
  const hasGroup = (requieredGroups: string[]): boolean =>
    requieredGroups.some((group) => myGroups.includes(group));

  const { data: projectQuery, isFetching: projectLoading, error: projectFetchError } = useGetApiProjectsIdId(projectId, {
    query: {
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.response?.status === 404) {
          return false;
        }
        return failureCount < 2;
      },
    },
  });
  const [projectInfo, setProjectInfo] = useState<ProjectDto | undefined>();
  const [preWorkControlData, setPreWorkControlData] = useState<KontrollCreateDto | undefined>();

  useEffect(() => {
    if (projectFetchError) {
      setProjectNotFoundError(projectFetchError);
      setProjectInfo(undefined);
    } else if (projectQuery) {
      setProjectInfo(projectQuery as ProjectDto);
      setProjectNotFoundError(null);
    }
  }, [projectQuery, projectFetchError]);

  const projectSuccessfullyFetched = !!projectInfo && !projectNotFoundError;
  const hasMonteringsanalys = resolveHasMonteringsanalys({
    hasMonteringsanalys: projectInfo?.hasMonteringsanalys,
  });

  const { data: projectStatus, refetch: refetchProjectStatus } = useGetApiProjectsGetProjectStatusProjectId(projectId, {
    query: {
      enabled: projectSuccessfullyFetched,
      refetchOnWindowFocus: false,
    },
  });

  useEffect(() => {
    if (!projectSuccessfullyFetched) return;
    getPreWorkControl(projectId)
      .then(setPreWorkControlData)
      .catch(() => setPreWorkControlData(undefined));
  }, [projectId, projectSuccessfullyFetched]);

  const [tableOfContentData, tableOfContentLoading] = useQueryWithState<TableOfContents>(
    ['tableOfContents', projectId],
    () => getApiTableOfContentsProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [rescueplanData, rescuePlanLoading, rescuePlanError, setRescueplanData] = useQueryWithState<RescuePlanModel>(
    ['rescuePlanItem', projectId],
    () => getApiRescueplanProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [completionDefinitions, completionDefinitionsLoading, completionDefinitionsError] = useQueryWithState<WorkPreparationCompletionContract>(
    ['workPreparationCompletionDefinitions'],
    () => getWorkPreparationCompletionDefinitions(),
    { enabled: projectSuccessfullyFetched, staleTime: Infinity, refetchOnWindowFocus: false },
  );

  const [riskAssesmentContainerData, riskAssesmentContainerLoading, riskAssesmentError, setRiskAssesmentContainerData] = useQueryWithState<RiskAssesmentContainer>(
    ['riskAssesmentContainer', projectId],
    () => getApiRiskAssesmentProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [factsAboutScaffoldingData, factAboutScaffoldingLoading, factAboutScaffoldingError, setFactsAboutScaffoldingData, factsAboutScaffoldingQuery] = useQueryWithState<FactsAboutScaffolding>(
    ['factsAboutScaffolding', projectId, projectInfo?.department],
    () => getApiFactsAboutScaffoldingProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [selfControlContainerData, selfControlContainerLoading, selfControlContainerError, setSelfControlContainerData] = useQueryWithState<SelfControlContainerModel>(
    ['selfControlContainer', projectId, projectInfo?.department],
    () => getApiSelfControlContainerProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [planForConductData, planForConductLoading, planForConductError, setPlanForConductData] = useQueryWithState<PlanForConduct>(
    ['planForConduct', projectId],
    () => getApiPlanForConductProjectId(projectId),
    { enabled: projectSuccessfullyFetched, refetchOnWindowFocus: false }
  );

  const [factsAboutWeatherProtectionData, factsAboutWeatherProtectionLoading, factsAboutWeatherProtectionError, setFactsAboutWeatherProtectionData, factsAboutWeatherProtectionQuery] = useQueryWithState<FactsAboutWeatherProtection | null>(
    ['factsaboutweatherprotection', projectId, projectInfo?.department],
    () => getApiFactsAboutWeatherProtectionProjectId(projectId),
    { enabled: projectSuccessfullyFetched && projectInfo?.department === 3, refetchOnWindowFocus: false }
  );

  const [selfControlWeatherProtectionData, selfControlWeatherProtectionLoading, selfControlWeatherProtectionError, setSelfControlWeatherProtectionData] = useQueryWithState<SelfControlWeatherProtectionContainerModel | null>(
    ['selfcontrolweatherprotection', projectId, projectInfo?.department],
    () => getApiSelfControlWeatherProtectionContainerProjectId(projectId),
    { enabled: projectSuccessfullyFetched && projectInfo?.department === 3, refetchOnWindowFocus: false }
  );

  const [monteringsanalysData, monteringsanalysLoading, monteringsanalysError, setMonteringsanalysData] = useQueryWithState<MonteringsanalysModel>(
    ['monteringsanalys', projectId],
    () => getApiMonteringsanalysProjectId(projectId),
    { enabled: projectSuccessfullyFetched && hasMonteringsanalys, refetchOnWindowFocus: false }
  );

  const [workPreparation, setWorkpreparation] = useState<WorkPreparationState>({});

  useEffect(() => {
    if (selfControlContainerData?.isComplete !== undefined) {
      setSelfControlCompletionStatus(selfControlContainerData.isComplete === true);
    }
  }, [selfControlContainerData?.isComplete]);

  useEffect(() => {
    if (factsAboutScaffoldingData?.isComplete !== undefined) {
      setFactAboutScaffoldingCompletionStatus(factsAboutScaffoldingData.isComplete === true);
    }
  }, [factsAboutScaffoldingData?.isComplete]);

  useEffect(() => {
    if (rescueplanData?.isComplete !== undefined) {
      setRescuePlanCompletionStatus(rescueplanData.isComplete === true);
    }
  }, [rescueplanData?.isComplete]);

  useEffect(() => {
    if (planForConductData?.isComplete !== undefined) {
      setPlanForConductCompletionStatus(planForConductData.isComplete === true);
    }
  }, [planForConductData?.isComplete]);

  useEffect(() => {
    if (riskAssesmentContainerData?.isComplete !== undefined) {
      setRiskAssesmentCompletionStatus(riskAssesmentContainerData.isComplete === true);
    }
    if (riskAssesmentContainerData?.isWorkerComplete !== undefined) {
      setRiskAssesmentWorkerCompletionStatus(riskAssesmentContainerData.isWorkerComplete === true);
    }
  }, [riskAssesmentContainerData?.isComplete, riskAssesmentContainerData?.isWorkerComplete]);

  useEffect(() => {
    if (factsAboutWeatherProtectionData?.isComplete !== undefined) {
      setFactsWeatherProtectionCompletionStatus(factsAboutWeatherProtectionData.isComplete === true);
    }
  }, [factsAboutWeatherProtectionData?.isComplete]);

  useEffect(() => {
    if (selfControlWeatherProtectionData?.isComplete !== undefined) {
      setselfControlWeatherProtectionCompletionStatus(selfControlWeatherProtectionData.isComplete === true);
    }
  }, [selfControlWeatherProtectionData?.isComplete]);

  useEffect(() => {
    setFactsAboutScaffoldingData((current) =>
      current && !Utils.hasValue(current.typeOfScaffolding)
        ? { ...current, typeOfScaffolding: 'Layher Allround' }
        : current
    );
  }, [factsAboutScaffoldingData?.typeOfScaffolding, setFactsAboutScaffoldingData]);

  useEffect(() => {
    setPlanForConductData((current) => {
      if (!current?.finishedScaffoldInfo || Utils.hasValue(current.finishedScaffoldInfo.prefabricatedScaffold)) {
        return current;
      }

      return {
        ...current,
        finishedScaffoldInfo: {
          ...current.finishedScaffoldInfo,
          prefabricatedScaffold: 'Layher Allround',
        },
      };
    });
  }, [planForConductData?.finishedScaffoldInfo?.prefabricatedScaffold, setPlanForConductData]);

  const projectNumber = projectInfo?.projectNumber;
  const customerName = projectInfo?.customer?.name;
  const sharePointFolderId = projectInfo?.sharePointFolderId;

  const buildSnapshot = useCallback((overrides?: Partial<WorkPreparationState>): WorkPreparationState => {
    return buildWorkPreparationSnapshot({
      tableOfContentData,
      rescueplanData,
      riskAssesmentContainerData,
      factsAboutScaffoldingData,
      selfControlContainerData,
      planForConductData,
      monteringsanalysData,
      factsAboutWeatherProtectionData,
      selfControlWeatherProtectionData,
      preWorkControlData,
      projectNumber,
      customer: customerName,
      sharePointFolderId,
      isWeatherProtectionProject: projectInfo?.department === Department.WeatherProtection,
    }, overrides);
  }, [
    tableOfContentData,
    rescueplanData,
    riskAssesmentContainerData,
    factsAboutScaffoldingData,
    selfControlContainerData,
    planForConductData,
    monteringsanalysData,
    factsAboutWeatherProtectionData,
    selfControlWeatherProtectionData,
    preWorkControlData,
    projectNumber,
    customerName,
    sharePointFolderId,
    projectInfo?.department,
  ]);

  const generateDocuments = async (
    updateProjectInDb: boolean = true,
    workPreparationSnapshot?: WorkPreparationState
  ) => {
    const latestPreWorkControlPromise = getPreWorkControl(projectInfo!.id!)
      .catch(() => undefined);
    const savePromise = updateProjectInDb ? save() : Promise.resolve();
    const [latestPreWorkControl] = await Promise.all([
      latestPreWorkControlPromise,
      savePromise,
    ]);

    let sharepointFolderId = await postApiSharepointPostProjectId(projectInfo!.id!.toString());
    const payloadToUpload = workPreparationSnapshot ?? buildSnapshot({
      preWorkControlData: latestPreWorkControl
    });

    if (payloadToUpload != null && sharepointFolderId != null) {
      const workPreparationWithSharepoint = {
        ...payloadToUpload,
        sharePointFolderId: sharepointFolderId
      };

      projectInfo!.sharePointFolderId = sharepointFolderId;
      setProjectInfo({ ...projectInfo!, sharePointFolderId: sharepointFolderId });
      setWorkpreparation(workPreparationWithSharepoint);

      await generateWorkPreparationDocuments(applyWorkPrepDateFallbacks(workPreparationWithSharepoint));
    }
  };

  const print = async () => {
    await printWorkPreparationDocuments({ ...applyWorkPrepDateFallbacks(workPreparation), sharePointFolderId: projectInfo?.sharePointFolderId ?? undefined });
  };

  const hasManagementAccess = hasGroup([groupAccess.arbetsledning, groupAccess.admin]);
  const hasWorkerAccess = hasGroup([groupAccess.worker]);
  const hasBothAccesses = hasManagementAccess && hasWorkerAccess;
  const workerView = hasBothAccesses
    ? userRole === 'worker'
    : !hasManagementAccess;
  const managementView = hasBothAccesses
    ? userRole === 'management'
    : hasManagementAccess;

  const persistRiskAssessment = async (): Promise<boolean> => {
    const response = await handleRiskAssesmentDataUpdate(riskAssesmentContainerData, workerView);
    setRiskAssesmentContainerData(response);
    const managementComplete = response.isComplete === true;
    const workerComplete = response.isWorkerComplete === true;
    setRiskAssesmentCompletionStatus(managementComplete);
    setRiskAssesmentWorkerCompletionStatus(workerComplete);
    return workerView ? workerComplete : managementComplete;
  };

  const canSignSelfControls = isReadyToSignSelfControls({
    department: projectInfo?.department,
    selfControlComplete: selfControlCompletionStatus,
    weatherProtectionSelfControlComplete: selfControlWeatherProtectionCompletionStatus,
    riskAssessmentComplete: riskAssesmentCompletionStatus,
  });

  const signSelfControl = async (weatherProtection: boolean) => {
    const selfControlUpdate = weatherProtection
      ? handleSelfControlWeatherProtectionDataUpdate(
        selfControlWeatherProtectionData,
        setselfControlWeatherProtectionCompletionStatus,
      )
      : handleSelfControlDataUpdate(
        selfControlContainerData,
        setSelfControlCompletionStatus,
      );
    await Promise.all([
      selfControlUpdate,
      persistRiskAssessment(),
    ]);

    const readiness = await getWorkPreparationReadiness(projectId);
    const canSign = weatherProtection
      ? readiness.canSignWeatherProtectionSelfControl
      : readiness.canSignScaffoldingSelfControl;
    if (!canSign) {
      const reason = readiness.reasons?.[0] ?? "Egenkontroller är inte klara än, signera igen när egenkontroller är klara";
      toastInfo(reason);
      return;
    }

    const result = weatherProtection
      ? await postWeatherProtectionSelfControlSignature(projectId)
      : await postScaffoldingSelfControlSignature(projectId);

    const refreshPromise = Promise.all([
      refetchProjectStatus(),
      factsAboutScaffoldingQuery.refetch(),
      ...(projectInfo?.department === Department.WeatherProtection
        ? [factsAboutWeatherProtectionQuery.refetch()]
        : []),
    ]);

    if (result.allRequiredSelfControlsSigned) {
      await Promise.all([postApiEmailNotifyProjectContact({
        projectId,
        workerName: activeAccount?.name ?? "",
        workerMail: activeAccount?.username ?? "",
      }), refreshPromise]);
      toastSuccess("Egenkontrollerna är signerade och e-post har skickats till projektets kontaktperson");
      if (result.documentGenerationError) {
        toastError("PDF-genereringen misslyckades", new Error(result.documentGenerationError));
      }
    } else {
      await refreshPromise;
      toastInfo("Egenkontrollen är signerad. På väderskyddsprojekt skickas e-post när båda egenkontrollerna är signerade.");
    }
  };

  const signScaffoldingSelfControl = () => signSelfControl(false);
  const signWeatherProtectionSelfControl = () => signSelfControl(true);

  const endProject = async () => {
    const result = await changeProjectStatus(projectId, ProjectStatus.Closed);
    if (result === false) {
      toastInfo("Projekt är redan avslutat");
    } else {
      toastSuccess("Projektet har avslutats");
    }
    await refetchProjectStatus();
  };

  const refetchStatusAfterSave = async () => {
    const previousStatus = projectStatus;
    const result = await refetchProjectStatus();
    const nextStatus = result.data;
    if (
      nextStatus !== undefined &&
      previousStatus !== undefined &&
      nextStatus > previousStatus
    ) {
      toastSuccess("Projektstatus har uppdaterats");
    }
  };

  const becameComplete = (wasComplete: boolean, isComplete: boolean) =>
    !wasComplete && isComplete;

  const notifyApproveResult = (sectionName: string, wasComplete: boolean, isComplete: boolean) => {
    if (becameComplete(wasComplete, isComplete)) {
      toastSuccess(`${sectionName} godkänd`);
    } else {
      toastInfo('Sparat');
    }
  };

  const save = async (_signSelfControls: boolean = false) => {
    const newlyCompletedSections: string[] = [];
    const trackSectionSave = async (
      sectionName: string,
      wasComplete: boolean,
      update: Promise<boolean>,
    ) => {
      const isComplete = await update;
      if (becameComplete(wasComplete, isComplete)) {
        newlyCompletedSections.push(sectionName);
      }
    };

    const updates: Promise<unknown>[] = [
      postApiTableOfContents(tableOfContentData!),
      trackSectionSave(
        'Egenkontroll Ställning',
        selfControlCompletionStatus,
        handleSelfControlDataUpdate(
          selfControlContainerData,
          setSelfControlCompletionStatus,
        ),
      ),
      trackSectionSave(
        'Riskbedömning',
        workerView ? riskAssesmentWorkerCompletionStatus : riskAssesmentCompletionStatus,
        persistRiskAssessment(),
      ),
    ];

    if (projectInfo?.department === 3) {
      updates.push(
        trackSectionSave(
          'Egenkontroll Väderskydd',
          selfControlWeatherProtectionCompletionStatus,
          handleSelfControlWeatherProtectionDataUpdate(
            selfControlWeatherProtectionData,
            setselfControlWeatherProtectionCompletionStatus,
          ),
        ),
      );
      if (!_signSelfControls) {
        updates.push(
          trackSectionSave(
            'Fakta om Väderskydd',
            factsAboutWeatherProtectionCompletionStatus,
            handleFactsWeatherProtectionDataUpdate(
              factsAboutWeatherProtectionData,
              setFactsWeatherProtectionCompletionStatus,
            ),
          ),
        );
      }
    }

    if (!_signSelfControls) {
      updates.push(
        trackSectionSave(
          'Fakta om ställning',
          factAboutScaffoldingCompletionStatus,
          handleFactAboutScaffoldingDataUpdate(
            factsAboutScaffoldingData,
            setFactAboutScaffoldingCompletionStatus,
          ),
        ),
        trackSectionSave(
          'Räddningsplan',
          rescuePlanCompletionStatus,
          handleRescuePlanDataUpdate(
            rescueplanData,
            setRescuePlanCompletionStatus,
          ),
        ),
        trackSectionSave(
          'Plan för uppförande',
          planForConductCompletionStatus,
          handlePlanForConductDataUpdate(
            planForConductData,
            setPlanForConductCompletionStatus,
          ),
        ),
      );
    }

    await Promise.all(updates);
    toastInfo('Sparat');
    newlyCompletedSections.forEach((sectionName) => {
      toastSuccess(`${sectionName} godkänd`);
    });
    await refetchStatusAfterSave();
  };

  useEffect(() => {
    setWorkpreparation(buildSnapshot());
    setDimensionDocUpdated(projectInfo?.dimensionDocUpdated ?? false);
  }, [projectInfo, buildSnapshot]);

  const handleMonteringsanalysSave = async (data: MonteringsanalysModel) => {
    try {
      const result = await putApiMonteringsanalys(data);
      setMonteringsanalysData(result);
    } catch (error) {
      console.error("Error saving monteringsanalys:", error);
      throw error;
    }
  };

  const handleMonteringsanalysSign = async (signProjectId: number, signedByName: string) => {
    try {
      const result = await postApiMonteringsanalysSignProjectId(signProjectId);

      if (result.success) {
        toastSuccess("Monteringsanalys signerad och PDF skapad!");

        const updatedData = await getApiMonteringsanalysProjectId(signProjectId);
        setMonteringsanalysData(updatedData);

        if (activeAccount) {
          await postApiEmailNotifyTeamLeader({
            projectId: signProjectId,
            workerName: signedByName,
            workerMail: activeAccount.username ?? ''
          });

          toastSuccess("E-post skickat till arbetsledare om signering av Monteringsanalys");
        }
      } else {
        toastInfo(result.errorMessage ?? result.message ?? "Okänt fel vid signering");
      }
    } catch (error) {
      console.error("Error signing monteringsanalys:", error);
      toastError("Fel vid signering av monteringsanalys", error);
    }
  };

  const approveRiskAssessment = async () => {
    const wasComplete = workerView
      ? riskAssesmentWorkerCompletionStatus
      : riskAssesmentCompletionStatus;
    const isComplete = await persistRiskAssessment();
    notifyApproveResult('Riskbedömning', wasComplete, isComplete);
    await refetchStatusAfterSave();
  };

  const approveRescuePlan = async () => {
    const wasComplete = rescuePlanCompletionStatus;
    const isComplete = await handleRescuePlanDataUpdate(
      rescueplanData,
      setRescuePlanCompletionStatus,
    );
    notifyApproveResult('Räddningsplan', wasComplete, isComplete);
    await refetchStatusAfterSave();
  };

  const approvePlanForConduct = async () => {
    const wasComplete = planForConductCompletionStatus;
    const isComplete = await handlePlanForConductDataUpdate(
      planForConductData,
      setPlanForConductCompletionStatus,
    );
    notifyApproveResult('Plan för uppförande', wasComplete, isComplete);
    await refetchStatusAfterSave();
  };

  const archived = isArchivedProject({
    status: projectStatus,
  });
  const closedOrHigher = isClosedOrHigher({
    status: projectStatus,
  });

  const loading = projectLoading || (
    tableOfContentLoading ||
    selfControlContainerLoading ||
    rescuePlanLoading ||
    completionDefinitionsLoading ||
    riskAssesmentContainerLoading ||
    factAboutScaffoldingLoading ||
    planForConductLoading ||
    monteringsanalysLoading ||
    (projectInfo?.department === Department.WeatherProtection && (factsAboutWeatherProtectionLoading || selfControlWeatherProtectionLoading))
  );

  const isWeatherProtectionProject = projectInfo?.department === Department.WeatherProtection;

  return {
    loading,
    error: selfControlContainerError
      || factAboutScaffoldingError
      || factsAboutWeatherProtectionError
      || selfControlWeatherProtectionError
      || rescuePlanError
      || completionDefinitionsError
      || planForConductError
      || riskAssesmentError
      || monteringsanalysError,
    notFound: !!projectNotFoundError,
    project: projectInfo,
    status: projectStatus,
    readiness: {
      canSignSelfControls,
      selfControlComplete: selfControlCompletionStatus,
      weatherProtectionSelfControlComplete: selfControlWeatherProtectionCompletionStatus,
      riskAssessmentComplete: riskAssesmentCompletionStatus,
      rescuePlanComplete: rescuePlanCompletionStatus,
      planForConductComplete: planForConductCompletionStatus,
      factAboutScaffoldingComplete: factAboutScaffoldingCompletionStatus,
      factsAboutWeatherProtectionComplete: factsAboutWeatherProtectionCompletionStatus,
    },
    sections: {
      tableOfContents: {
        projectInfo,
        tableOfContentData,
      },
      preStartCheck: {
        projectId,
        isWeatherProtectionProject,
      },
      weatherProtection: {
        projectId,
        factsAboutWeatherProtectionData,
        factsAboutWeatherProtectionCompletionStatus,
        setFactsAboutWeatherProtectionData,
        selfControlWeatherProtectionData,
        selfControlWeatherProtectionCompletionStatus,
        setSelfControlWeatherProtectionData,
        error: selfControlWeatherProtectionError,
        weatherProtectionFactsError: factsAboutWeatherProtectionError,
      },
      scaffolding: {
        factsAboutScaffoldingData,
        setFactsAboutScaffoldingData,
        factAboutScaffoldingCompletionStatus,
        error: factAboutScaffoldingError,
      },
      selfControl: {
        projectId,
        selfControlContainerData,
        setSelfControlContainerData,
        selfControlCompletionStatus,
        error: selfControlContainerError,
      },
      monteringsanalys: {
        monteringsanalysData,
        projectInfo,
        tableOfContentData,
        onSave: handleMonteringsanalysSave,
        projectId,
        onSign: handleMonteringsanalysSign,
        available:
          workerView || (
            selfControlCompletionStatus &&
            (projectInfo?.department !== Department.WeatherProtection || selfControlWeatherProtectionCompletionStatus)
          ),
        completionRule: completionDefinitions?.monteringsanalys,
        error: monteringsanalysError,
      },
      riskAssessment: {
        riskAssesmentContainerData,
        riskAssesmentCompletionStatus: workerView
          ? riskAssesmentWorkerCompletionStatus
          : riskAssesmentCompletionStatus,
        setRiskAssesmentContainerData,
        onApprove: approveRiskAssessment,
        error: riskAssesmentError,
      },
      rescuePlan: {
        rescueplanData,
        setRescueplanData,
        rescuePlanCompletionStatus,
        onApprove: approveRescuePlan,
        error: rescuePlanError,
      },
      planForConduct: {
        planForConductData,
        setPlanForConductData,
        planForConductCompletionStatus,
        isWeatherProtectionProject,
        onApprove: approvePlanForConduct,
        error: planForConductError,
      },
      dimensionDocuments: {
        projectInfo,
        dimensionDocUpdated,
        setDimensionDocUpdated,
      },
    },
    workerView,
    managementView,
    isArchived: archived,
    hasMonteringsanalys,
    hasBothAccesses,
    userRole,
    setUserRole,
    signScaffoldingSelfControl,
    signWeatherProtectionSelfControl,
    save: () => save(),
    endProject,
    disableAllExceptPrint: closedOrHigher,
    generateDocuments: () => generateDocuments(),
    print,
    workPreparation,
  };
};
